import { DdsRefs } from "@/components/dds";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { fmtDate, fmtMoney, fmtNum } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { PageHeader } from "@/components/common";
import DataTable, { useDebounced } from "@/components/DataTable";
import { ActionBanner } from "@/components/ActionBanner";
import { PoPlant, PLANT_SOURCES } from "@/components/PoPlant";
import { SourcingBadge, SourcingSelect } from "@/components/ProductRequest";
import { ClearanceChip, ClearanceFilter, ClearanceCounts, BulkClearance } from "@/components/Clearance";

const mono = (k) => (r) => <span className="font-mono text-[13px] text-slate-900">{r[k] || "—"}</span>;
const num = (k) => (r) => fmtNum(r[k], 2);
const right = { className: "text-right", thClass: "text-right" };

const CONFIG = {
  vendors: {
    title: "Vendors", subtitle: "SAP vendor master. Placeholder accounts (“DO NOT USE”) are flagged.", inScope: "Only vendors supplying in-scope materials",
    columns: [
      { key: "vendor", label: "Vendor", sortable: true, render: mono("vendor") },
      { key: "name1", label: "Name", sortable: true, render: (r) => <span className="font-medium text-slate-800">{r.name1}</span> },
      { key: "name2", label: "Name 2", className: "max-w-[220px] truncate" },
      { key: "country", label: "Country", sortable: true },
      { key: "city", label: "City", sortable: true },
      { key: "postal_code", label: "Postcode" },
      { key: "search_term", label: "Search term" },
      { key: "placeholder", label: "", render: (r) => r.placeholder && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">PLACEHOLDER</span> },
    ],
  },
  customers: {
    title: "Customers", subtitle: "SAP customer master (40k+ records, server-side paginated).",
    columns: [
      { key: "customer", label: "Customer", sortable: true, render: mono("customer") },
      { key: "name1", label: "Name", sortable: true, render: (r) => <span className="font-medium text-slate-800">{r.name1}</span> },
      { key: "name2", label: "Name 2", className: "max-w-[220px] truncate" },
      { key: "country", label: "Country", sortable: true },
      { key: "city", label: "City", sortable: true },
      { key: "postal_code", label: "Postcode" },
      { key: "street", label: "Street", className: "max-w-[200px] truncate" },
      { key: "telephone", label: "Telephone" },
      { key: "clearance", label: "Import clearance", render: (r) => <ClearanceChip r={r} /> },
      { key: "dds_refs", label: "DDS refs", render: (r) => <DdsRefs refs={r.dds_refs} testid={`customer-dds-${r.customer}`} /> },
      { key: "one_time", label: "", render: (r) => r.one_time && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">ONE-TIME</span> },
    ],
  },
  po_lines: {
    title: "PO lines", subtitle: "Purchase order lines (76 SAP columns kept). GBP value = SAP “Value” converted with the PO exchange rate.", inScope: "Only PO lines for in-scope materials",
    columns: [
      { key: "po_no", label: "PO No.", sortable: true, render: mono("po_no") },
      { key: "record_id", label: "Record ID", render: (r) => <span className="font-mono text-xs">{r.record_id}</span> },
      { key: "po_date", label: "PO date", sortable: true, render: (r) => fmtDate(r.po_date) },
      { key: "vendor_name", label: "Vendor", sortable: true, className: "max-w-[200px] truncate", render: (r) => <span title={r.vendor_name}><span className="font-mono text-xs text-slate-400">{r.vendor_id}</span> {r.vendor_name}</span> },
      { key: "vendor_country", label: "Ctry" },
      { key: "material", label: "Material", sortable: true, render: mono("material") },
      { key: "plant", label: "Plant", sortable: true, render: (r) => <PoPlant r={r} testid={`po-plant-${r.record_id}`} /> },
      { key: "flow_label", label: "Flow", className: "max-w-[160px] truncate", render: (r) => <span className="text-xs" title={r.flow_label}>{r.flow_label || "—"}</span> },
      { key: "description", label: "Description", className: "max-w-[220px] truncate" },
      { key: "po_qty", label: "Qty", ...right, render: (r) => `${fmtNum(r.po_qty)} ${r.uom || ""}` },
      { key: "value", label: "PO value", sortable: true, ...right, render: (r) => fmtMoney(r.value, r.currency || "GBP") },
      { key: "value_gbp", label: "GBP", sortable: true, ...right, render: (r) => fmtMoney(r.value_gbp) },
      { key: "delivery_date", label: "Delivery", sortable: true, render: (r) => fmtDate(r.delivery_date) },
      { key: "dds_refs", label: "DDS refs", render: (r) => <DdsRefs refs={r.dds_refs} testid={`po-dds-${r.record_id}`} /> },
    ],
  },
  stock: {
    title: "Stock", subtitle: "Stock by material, plant and storage location.", plant: true, inScope: "Only stock of in-scope materials",
    columns: [
      { key: "material", label: "Material", sortable: true, render: mono("material") },
      { key: "plant", label: "Plant", sortable: true, render: mono("plant") },
      { key: "storage_location", label: "SLoc", sortable: true },
      { key: "unrestricted", label: "Unrestricted", sortable: true, ...right, render: num("unrestricted") },
      { key: "uom", label: "UoM" },
      { key: "value_unrestricted", label: "Value", sortable: true, ...right, render: (r) => fmtMoney(r.value_unrestricted, r.currency || "GBP") },
      { key: "transit", label: "In transit", ...right, render: num("transit") },
      { key: "quality_insp", label: "QI", ...right, render: num("quality_insp") },
      { key: "blocked", label: "Blocked", ...right, render: num("blocked") },
    ],
  },
  sales: {
    title: "Sales", subtitle: "SAP billing lines (standard columns: Billing Doc, Item, Billing Date, Sold-to, Ship-to, Country, Material, Plant, Qty, UoM, Net Value, Currency, Sales Order, Delivery).", plant: true,
    columns: [
      { key: "flow_label", label: "EUDR flow", render: (r) => <span className="text-xs" title={(r.flags || []).join(", ")}>{r.flow_label || "—"}{r.flags?.length ? <span className="ml-1 text-amber-700">· {r.flags.join(", ")}</span> : null}</span> },
      { key: "sourcing", label: "Sourcing", render: (r) => <SourcingBadge s={r.sourcing} testid={`sales-sourcing-${r.billing_doc}-${r.item}`} /> },
      { key: "dds_refs", label: "DDS refs", render: (r) => <DdsRefs refs={r.dds_refs} testid={`sales-dds-${r.billing_doc}-${r.item}`} /> },
      { key: "billing_doc", label: "Billing doc", render: mono("billing_doc") },
      { key: "item", label: "Item" },
      { key: "billing_date", label: "Date", sortable: true, render: (r) => fmtDate(r.billing_date) },
      { key: "sold_to", label: "Sold-to" },
      { key: "ship_to", label: "Ship-to" },
      { key: "country", label: "Country" },
      { key: "material", label: "Material", sortable: true, render: mono("material") },
      { key: "plant", label: "Plant", sortable: true, render: mono("plant") },
      { key: "qty", label: "Qty", ...right, render: (r) => `${fmtNum(r.qty)} ${r.uom || ""}` },
      { key: "net_value", label: "Net value", sortable: true, ...right, render: (r) => fmtMoney(r.net_value, r.currency || "GBP") },
      { key: "sales_order", label: "Sales order" },
      { key: "delivery", label: "Delivery" },
    ],
  },
};

export default function DataPage() {
  const { type } = useParams();
  const [sp] = useSearchParams();
  const { params: plantParams } = usePlant();
  const cfg = CONFIG[type];
  const [q, setQ] = useState(sp.get("q") || "");
  const [country, setCountry] = useState("");
  const [inScope, setInScope] = useState(sp.get("in_scope") === "true");
  const [plantSource, setPlantSource] = useState(sp.get("plant_source") || "");
  const [sourcing, setSourcing] = useState(sp.get("sourcing") || "");
  const [clearance, setClearance] = useState(sp.get("clearance") || "");
  const [level, setLevel] = useState(sp.get("level") || "ship_to");
  const action = sp.get("action") || "";
  const [tick, setTick] = useState(0);
  const dq = useDebounced(q);
  const dc = useDebounced(country);
  const params = useMemo(() => {
    const p = { q: dq, in_scope: inScope || undefined, ...plantParams, ...(type === "po_lines" && plantSource ? { plant_source: plantSource } : {}), ...(type === "sales" && sourcing ? { sourcing } : {}), ...(type === "customers" && clearance ? { clearance, level } : {}), ...(type === "customers" && tick ? { _t: tick } : {}), ...(["customers", "sales"].includes(type) && action ? { action, stock: sp.get("stock") || "in" } : {}) };
    if (dc) p[type === "po_lines" ? "vendor_country" : "country"] = dc.toUpperCase();
    return p;
  }, [dq, dc, inScope, plantParams, type, plantSource, sourcing, clearance, level, tick, action, sp]);
  if (!cfg) return null;
  return (
    <div data-testid={`data-page-${type}`} key={type}>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} />
      {["customers", "sales"].includes(type) && <ActionBanner action={action} clearTo={`/data/${type}`} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid={`${type}-search-input`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 bg-white pl-9" />
        </div>
        {type !== "stock" && (
          <Input data-testid={`${type}-country-filter`} value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (e.g. GB)" className="h-9 w-40 bg-white uppercase" maxLength={3} />
        )}
        {type === "po_lines" && (
          <select data-testid="po_lines-plant-source-filter" value={plantSource} onChange={(e) => setPlantSource(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="">Any plant source</option>{Object.entries(PLANT_SOURCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        {type === "sales" && <SourcingSelect value={sourcing} onChange={setSourcing} testid="sales-sourcing-filter" />}
        {type === "customers" && <ClearanceFilter value={clearance} onChange={setClearance} testid="customers-clearance-filter" />}
        {type === "customers" && clearance && (
          <select data-testid="customers-clearance-level" value={level} onChange={(e) => setLevel(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="ship_to">Delivery accounts (ship-to)</option><option value="sold_to">Buying customers (sold-to)</option><option value="both">Both</option></select>)}
        {type === "customers" && <BulkClearance onDone={() => setTick((x) => x + 1)} />}
        {cfg.inScope && (
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">
            <Switch checked={inScope} onCheckedChange={setInScope} data-testid={`${type}-in-scope-toggle`} /> {cfg.inScope}
          </label>
        )}
      </div>
      {type === "customers" && clearance && <ClearanceCounts params={params} />}
      <DataTable endpoint={`/data/${type}`} params={params} columns={cfg.columns} testid={`${type}-table`} />
    </div>
  );
}
